import DfinityTitle from '@assets/icons/dfinity-title.webp'
import ICPSwapTitle from '@assets/icons/icpswap-title.png'
import JupiterTitle from '@assets/icons/jupiter-title.png'
import LifiTitle from '@assets/icons/lifi-title.png'
import OdinTitle from '@assets/icons/odin-title.png'
import OneSecTitle from '@assets/icons/onesec-title.png'
import StealthexTitle from '@assets/icons/stealthex-title.png'
import ChainLogo from '@components/ChainLogo'
import { Titlebar } from '@components/Titlebar/Titlebar'
import TokenAvatar from '@components/TokenAvatar'
import HyperliquidTitle from '@plug-core/assets/icons/hyperevm.svg'
import YusanTitle from '@plug-core/assets/icons/yusan.png'
import useTransactionOtherParty, { hasOtherParty } from '@plug-core/hooks/useTransactionOtherParty'
import { PlugToken } from '@plug-core/interfaces/common'
import { SwapProvider } from '@plug-core/interfaces/swapInterface'
import { PlugUserActivity, SwapActivity } from '@plug-core/interfaces/transactionActivity'
import { formatMoney, formatNumber } from '@plug-core/utils/formatNumber'
import { e8sToDecimals } from '@plug-core/utils/validationHelper'
import { Banner, Button, Heading, ListGroup, ListRow, Text, cn } from '@ui'
import { format } from 'date-fns'
import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { HiArrowRight } from 'react-icons/hi2'
import { TransactionOtherParty } from './TransactionOtherParty'

const SWAP_PROVIDER_TITLE_MAP = {
  [SwapProvider.ICPSWAP]: ICPSwapTitle,
  [SwapProvider.ODIN]: OdinTitle,
  [SwapProvider.JUPITER]: JupiterTitle,
  [SwapProvider.LIFI]: LifiTitle,
  [SwapProvider.STEALTHEX]: StealthexTitle,
  [SwapProvider.ONESEC]: OneSecTitle,
  [SwapProvider.DFINITY]: DfinityTitle,
  [SwapProvider.YUSAN]: YusanTitle,
  [SwapProvider.HYPERLIQUID]: HyperliquidTitle,
}

/**
 * Third-party brand backdrops for each provider's wordmark (the wordmarks are drawn for these
 * colours). They are partner brand data, not app theme colours, so they stay literal.
 */
const SWAP_PROVIDER_GRADIENT_MAP = {
  [SwapProvider.ICPSWAP]: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
  [SwapProvider.ODIN]: 'linear-gradient(135deg, rgb(0 99 99) 0%, rgb(0 49 49) 60%)',
  [SwapProvider.JUPITER]: 'linear-gradient(135deg, rgb(12 63 48) 1%, rgb(123 90 11) 100%)',
  [SwapProvider.LIFI]: 'linear-gradient(135deg, rgb(177 93 194) 1%, rgb(252 220 255) 100%)',
  [SwapProvider.STEALTHEX]: '#ffe500',
  [SwapProvider.ONESEC]: 'linear-gradient(135deg, #091bd1 0%, #337ef2 100%)',
  [SwapProvider.DFINITY]: 'linear-gradient(135deg, #091bd1 0%, #337ef2 100%)',
  [SwapProvider.YUSAN]: 'linear-gradient(135deg, #0f3d3f 0%, #0a6e6f 100%)',
  [SwapProvider.HYPERLIQUID]: 'linear-gradient(135deg, #072723 0%, #0a6e6f 100%)',
}

const Item = ({ label, value }: { label: string; value: ReactNode }) => {
  const { t } = useTranslation()

  return (
    <ListRow
      dense
      primary={
        <span className="font-medium text-fg-2">{t('transactionHistory.details' + label)}</span>
      }
    >
      <span className="min-w-0 flex-[2] text-right text-[15px] font-medium break-all text-fg">
        {value}
      </span>
    </ListRow>
  )
}

const TokenLogo = ({ token, size }: { token: PlugToken; size: 'large' | 'small' }) => (
  <span className="relative block">
    <TokenAvatar logo={token.logo} size={size === 'large' ? 60 : 52} />

    <span
      className={cn(
        'absolute -right-2 flex rounded-full bg-sheet p-[4px]',
        size === 'large' ? '-bottom-2' : '-bottom-2.5'
      )}
    >
      <ChainLogo chain={token.chain} size={size === 'large' ? 22 : 18} />
    </span>
  </span>
)

const Transaction = ({ activity }: { activity: PlugUserActivity }) => {
  const { t } = useTranslation()
  const { isCommissionWallet } = useTransactionOtherParty({ activity })

  return (
    <>
      <div className="flex flex-col items-center gap-4">
        <TokenLogo token={activity.sent.token} size="large" />

        <div className="flex flex-col items-center gap-0.5">
          <Heading
            level={3}
            align="center"
            tabular
            tone={activity.isOutgoing ? 'danger' : 'success'}
          >
            {activity.isOutgoing ? '-' : '+'}
            {formatNumber(e8sToDecimals(activity.sent.amount, activity.sent.token.decimals))}
          </Heading>
          <Text variant="body" weight="semibold" tone="fg-2">
            {activity.sent.token.symbol}
          </Text>
        </div>
      </div>

      <ListGroup className="mt-5">
        <Item label=".date" value={format(activity.timestamp, 'd MMMM yyyy')} />
        <Item label=".time" value={format(activity.timestamp, 'HH:mm:ss')} />

        {hasOtherParty(activity) && (
          <Item
            label={activity.isOutgoing ? '.to' : '.from'}
            value={<TransactionOtherParty activity={activity} copy={!isCommissionWallet} long />}
          />
        )}
      </ListGroup>

      {isCommissionWallet && (
        <Banner tone="warning" className="mt-4">
          {t('transactionHistory.commissionInfo')}
        </Banner>
      )}
    </>
  )
}

const SwapSide = ({ token, amount, sign }: { token: PlugToken; amount: bigint; sign: string }) => (
  <div className="flex flex-col items-center">
    <TokenLogo token={token} size="small" />

    <div className="mt-3 flex flex-col items-center">
      <Text
        as="span"
        variant="body-s"
        weight="semibold"
        tabular
        tone={sign === '+' ? 'success' : 'danger'}
      >
        {sign}
        {formatNumber(e8sToDecimals(amount, token.decimals))}
      </Text>
      <Text as="span" variant="body-s" weight="semibold" tone="fg-2">
        {token.symbol}
      </Text>
    </div>
  </div>
)

const Swap = ({ activity }: { activity: SwapActivity }) => {
  const fromChain = activity.sent.token.evmChain?.symbol || activity.sent.token.chain.toUpperCase()
  const toChain =
    activity.received.token.evmChain?.symbol || activity.received.token.chain.toUpperCase()

  return (
    <>
      <div
        className="flex items-center justify-center overflow-hidden rounded-lg"
        style={{ background: SWAP_PROVIDER_GRADIENT_MAP[activity.provider] }}
      >
        <img
          src={SWAP_PROVIDER_TITLE_MAP[activity.provider]}
          alt={activity.provider}
          className="h-[72px] max-w-full"
        />
      </div>

      <div className="mt-6 flex justify-center">
        <SwapSide token={activity.sent.token} amount={activity.sent.amount} sign="-" />

        <span className="mx-5 mt-4 flex text-fg-2">
          <HiArrowRight aria-hidden className="size-5" />
        </span>

        <SwapSide token={activity.received.token} amount={activity.received.amount} sign="+" />
      </div>

      <ListGroup className="mt-5">
        <Item label=".date" value={format(activity.timestamp, 'd MMMM yyyy')} />
        <Item label=".time" value={format(activity.timestamp, 'HH:mm:ss')} />

        {activity.sent.amountUsd && activity.received.amountUsd && (
          <Item
            label=".fiatValue"
            value={
              <span className="tabular">{`${formatMoney(activity.sent.amountUsd)} → ${formatMoney(activity.received.amountUsd)}`}</span>
            }
          />
        )}

        {fromChain !== toChain && <Item label=".bridgeRoute" value={`${fromChain} → ${toChain}`} />}
      </ListGroup>
    </>
  )
}

export const UserActivityDetails = ({
  activity,
  onClose,
}: {
  activity: PlugUserActivity
  onClose: () => void
}) => {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col">
      <Titlebar onActionClick={onClose} />

      {activity.isSwap ? <Swap activity={activity} /> : <Transaction activity={activity} />}

      {activity.link && (
        <Button
          variant="primary"
          size="lg"
          fullWidth
          className="mt-5"
          onClick={() => window.open(activity.link!)}
        >
          {t('transactionHistory.details.viewDetails')}
        </Button>
      )}
    </div>
  )
}
